import React, { useState, useCallback, useRef } from 'react';
import { sendRequest, describeApiError } from 'config/api';
import {
  fetchAllCourses,
  setCompanyCourses as setCompanyCoursesRequest,
  fetchCoursesInProgress,
  fetchAssignedCourses,
  fetchPlans,
  registerCompany,
  fetchCategories,
  updateCourse,
} from 'queries/Company';
import { createUser as createUserRequest } from 'queries/User';
import { useToast } from './toastContext';

const LearningSpaceContext = React.createContext(null);

export const LearningSpaceProvider = (props) => {
  const [learningSpaces, setLearningSpaces] = useState([]);
  const [loadingLearningSpace, setLoadingLearningSpace] = useState(false);
  const [learingSpaceDetails, setLearningSpaceDetails] = useState({});
  const [learningSpaceUsers, setLearningSpaceUsers] = useState({});
  const [loadingLearningSpaceUsers, setLoadingLearningSpaceUsers] =
    useState(false);
  const [deletingLearningSpace, setDeletingLearningSpace] = useState(false);
  const [editingLearningSpace, setEditingLearningSpace] = useState(false);
  const [updatingSubscription, setUpdatingSubscription] = useState(false);
  const [editingUser, setEditingUser] = useState(false);
  const [fetchingMoreLearningSpaces, setFetchingMoreLearningSpaces] =
    useState(false);
  const [courses, setCourses] = useState([]);
  const [companyCourses, setCompanyCourses] = useState([]);
  const [coursesInProgress, setCoursesInProgress] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [savingCourses, setSavingCourses] = useState(false);
  const [plans, setPlans] = useState([]);
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [creatingSpace, setCreatingSpace] = useState(false);
  const [categories, setCategories] = useState([]);
  const [savingCourse, setSavingCourse] = useState(false);

  const { toast } = useToast();
  const lastDomain = useRef(null);

  const getLearningSpaces = useCallback(async (searchParams = '') => {
    setLoadingLearningSpace(true);
    try {
      const { data } = await sendRequest(
        `/org/company/domains/?search=${searchParams}`
      );
      setLearningSpaces(data);
    } catch (error) {
      toast.error(`Couldn't load learning spaces - ${describeApiError(error)}`);
    }
    setLoadingLearningSpace(false);
  }, []);

  const learningSpacesFetchMore = async (pageNumber, searchParams = '') => {
    setFetchingMoreLearningSpaces(true);
    try {
      const { data } = await sendRequest(
        `/org/company/domains/?search=${searchParams}&page=${pageNumber}`
      );
      setLearningSpaces((prevState) => ({
        ...data,
        results: [...(prevState?.results ?? []), ...(data?.results ?? [])],
      }));
    } catch (error) {
      toast.error(
        `Couldn't load more learning spaces - ${describeApiError(error)}`
      );
    }
    setFetchingMoreLearningSpaces(false);
  };

  const isUuid = (value) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      value ?? ''
    );

  // The rich company payload (courses count, customizations, settings,
  // current_subscription) only comes from /org/company/data/?domain=, which
  // keys off the domain -- but the route only carries the company id, so an
  // id is resolved to its domain first. Callers with no argument refresh
  // whichever company was loaded last.
  const getLearningSpaceDetails = useCallback(async (companyIdOrDomain) => {
    setLoadingLearningSpace(true);
    try {
      let domain = companyIdOrDomain;

      if (isUuid(companyIdOrDomain)) {
        const { data: company } = await sendRequest(
          `/org/company/${companyIdOrDomain}/`
        );
        domain = company?.domain;
      }

      if (!domain) domain = lastDomain.current;
      if (!domain) return;

      lastDomain.current = domain;
      const { data } = await sendRequest(
        `/org/company/data/?domain=${encodeURIComponent(domain)}`
      );
      setLearningSpaceDetails(data);
    } catch (error) {
      toast.error(
        `Couldn't load learning space - ${describeApiError(error)}`
      );
    } finally {
      setLoadingLearningSpace(false);
    }
  }, []);

  const getLearningSpaceUsers = useCallback(
    async (userId, learningSpaceId, pageNumber = 1, itemsPerPage = 10) => {
      if (!learningSpaceId) return;
      setLoadingLearningSpaceUsers(true);
      try {
        // Scoped with ?company= rather than by PATCHing update_company, which
        // used to move the signed-in admin's OWN account into whichever space
        // they were viewing -- so they showed up in its user list.
        const { data } = await sendRequest(
          `/auth/users/?company=${learningSpaceId}&page=${pageNumber}&page_size=${itemsPerPage}`
        );
        setLearningSpaceUsers(data);
      } catch (error) {
        toast.error(`Couldn't load users - ${describeApiError(error)}`);
      }
      setLoadingLearningSpaceUsers(false);
    },
    []
  );

  const deleteLearningSpaces = useCallback((companyId) => {
    setDeletingLearningSpace(true);
    return sendRequest(`/org/company/${companyId}`, null, 'delete')
      .then(() => toast.success('Deleted successfully'))
      .catch((error) =>
        toast.error(`Couldn't delete learning space - ${describeApiError(error)}`)
      )
      .finally(() => setDeletingLearningSpace(false));
  }, []);

  const editLearningSpace = useCallback((companyId, editInfo) => {
    setEditingLearningSpace(true);
    return sendRequest(`/org/company/${companyId}/`, editInfo, 'patch')
      .then(() => {
        toast.success('Updated successfully');
        getLearningSpaceDetails(companyId);
      })
      .catch((error) =>
        toast.error(`Couldn't update learning space - ${describeApiError(error)}`)
      )
      .finally(() => setEditingLearningSpace(false));
  });

  const updateSubscription = useCallback((subscriptionId, subscriptionInfo) => {
    setUpdatingSubscription(true);
    return sendRequest(
      `/subscription/subscribe/${subscriptionId}/`,
      subscriptionInfo,
      'patch'
    )
      .then(() => {
        toast.success('Subscription updated');
        getLearningSpaceDetails();
      })
      .catch((error) =>
        toast.error(`Couldn't update learning space - ${describeApiError(error)}`)
      )
      .finally(() => setUpdatingSubscription(false));
  });

  const editUser = useCallback((userId, editInfo, userMode = 'ADMIN') => {
    setEditingUser(true);
    return sendRequest(
      `/auth/users/${userId}/?user_mode=${userMode}`,
      editInfo,
      'PATCH',
      'multipart/form-data'
    )
      .then(() => toast.success('User updated'))
      .catch((error) =>
        toast.error(`Couldn't update user - ${describeApiError(error)}`)
      )
      .finally(() => setEditingUser(false));
  }, []);

  // --- courses on a company -------------------------------------------------

  const getCourses = useCallback(async (includeInactive = false) => {
    setLoadingCourses(true);
    try {
      setCourses(await fetchAllCourses(includeInactive));
    } catch (error) {
      toast.error(`Couldn't load courses - ${describeApiError(error)}`);
    }
    setLoadingCourses(false);
  }, []);

  const getCompanyCourses = useCallback(async (companyId) => {
    if (!companyId) return;
    try {
      const { data } = await fetchAssignedCourses(companyId);
      setCompanyCourses(data ?? []);
    } catch (error) {
      toast.error(`Couldn't load company courses - ${describeApiError(error)}`);
    }
  }, []);

  // Which of the company's courses have at least one student with progress
  // in them -- used to warn before unassigning one still in use.
  const getCoursesInProgress = useCallback(async (companyId) => {
    if (!companyId) return;
    try {
      const { data } = await fetchCoursesInProgress(companyId);
      setCoursesInProgress(data?.course_ids ?? []);
    } catch (error) {
      toast.error(
        `Couldn't check course progress - ${describeApiError(error)}`
      );
    }
  }, []);

  const saveCompanyCourses = useCallback(async (companyId, courseIds) => {
    setSavingCourses(true);
    try {
      await setCompanyCoursesRequest(companyId, courseIds);
      toast.success('Courses updated');
      await getCompanyCourses(companyId);
      await getLearningSpaceDetails(companyId);
      return true;
    } catch (error) {
      toast.error(`Couldn't update courses - ${describeApiError(error)}`);
      return false;
    } finally {
      setSavingCourses(false);
    }
  }, []);

  // --- subscription plans ---------------------------------------------------

  const getPlans = useCallback(async () => {
    try {
      const { data } = await fetchPlans();
      setPlans(data?.results ?? data ?? []);
    } catch (error) {
      toast.error(`Couldn't load plans - ${describeApiError(error)}`);
    }
  }, []);

  // --- accounts -------------------------------------------------------------

  const createAccount = useCallback(async (payload, sendInvite = false, companyId) => {
    setCreatingAccount(true);
    try {
      await createUserRequest(payload, sendInvite, companyId);
      toast.success('User created');
      return true;
    } catch (error) {
      toast.error(`Couldn't create user - ${describeApiError(error)}`);
      return false;
    } finally {
      setCreatingAccount(false);
    }
  }, []);

  const createLearningSpace = useCallback(async (payload) => {
    setCreatingSpace(true);
    try {
      const { data } = await registerCompany(payload);
      toast.success(`${payload.company.name} created`);
      await getLearningSpaces();
      return data;
    } catch (error) {
      toast.error(`Couldn't create learning space - ${describeApiError(error)}`);
      return null;
    } finally {
      setCreatingSpace(false);
    }
  }, []);

  const getCategories = useCallback(async () => {
    try {
      const { data } = await fetchCategories();
      setCategories(data?.results ?? data ?? []);
    } catch (error) {
      toast.error(`Couldn't load categories - ${describeApiError(error)}`);
    }
  }, []);

  const saveCourse = useCallback(async (courseId, payload) => {
    setSavingCourse(true);
    try {
      const { data } = await updateCourse(courseId, payload);
      // Patch the row in place so the list does not have to refetch.
      setCourses((prev) =>
        prev.map((course) =>
          course.id === courseId ? { ...course, ...(data ?? payload) } : course
        )
      );
      return true;
    } catch (error) {
      toast.error(`Couldn't update course - ${describeApiError(error)}`);
      return false;
    } finally {
      setSavingCourse(false);
    }
  }, []);

  const value = {
    data: {
      learningSpaces,
      loadingLearningSpace,
      learingSpaceDetails,
      loadingLearningSpaceUsers,
      learningSpaceUsers,
      deletingLearningSpace,
      editingLearningSpace,
      fetchingMoreLearningSpaces,
      updatingSubscription,
      editingUser,
      courses,
      companyCourses,
      coursesInProgress,
      loadingCourses,
      savingCourses,
      plans,
      creatingAccount,
      creatingSpace,
      categories,
      savingCourse,
    },
    actions: {
      getLearningSpaces,
      getLearningSpaceDetails,
      getLearningSpaceUsers,
      deleteLearningSpaces,
      editLearningSpace,
      updateSubscription,
      editUser,
      learningSpacesFetchMore,
      getCourses,
      getCompanyCourses,
      getCoursesInProgress,
      saveCompanyCourses,
      getPlans,
      createAccount,
      createLearningSpace,
      getCategories,
      saveCourse,
    },
  };
  return (
    <LearningSpaceContext.Provider value={value}>
      {props.children}
    </LearningSpaceContext.Provider>
  );
};

export const useLearningSpaceContext = () =>
  React.useContext(LearningSpaceContext);

export default LearningSpaceContext;
